import { useState } from "react";
import { Music2, Send, ExternalLink, Instagram, Youtube, Disc3 } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const genres = ["Arrocha", "Arrochadeira", "Brega", "Seresta", "Forró", "Pagode", "Sertanejo", "Outro"];
const submissionTypes = ["CD", "Música", "EP", "DVD", "Notícia"];
const states = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function DivulguePage() {
  const [form, setForm] = useState({
    name: "", band_name: "", email: "", whatsapp: "", city: "", state: "BA",
    genre: "Arrocha", sua_musica_url: "", spotify_url: "", instagram_url: "",
    youtube_url: "", message: "", submission_type: "CD"
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.band_name || !form.email) {
      toast.error("Preencha nome, nome da banda e e-mail!");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSent(true);
        toast.success("Enviado com sucesso!");
      } else {
        toast.error("Erro ao enviar. Tente novamente.");
      }
    } catch {
      toast.error("Erro de conexão.");
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-[#FF007F]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-10 h-10 text-[#FF007F]" />
          </div>
          <h1 className="text-3xl font-black mb-4">Enviado com Sucesso!</h1>
          <p className="text-zinc-400 text-lg mb-8">
            Sua solicitação de divulgação foi recebida. Nossa equipe vai analisar e entrar em contato em breve.
          </p>
          <Link to="/" className="px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all" data-testid="back-home-btn">
            Voltar ao Início
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF007F]/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto px-4 py-12 relative">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-full mb-6">
              <Disc3 className="w-4 h-4 text-[#FF007F]" />
              <span className="text-sm text-[#FF007F] font-semibold">Divulgação Gratuita</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black mb-4">
              Divulgue sua <span className="text-[#FF007F]">Banda</span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Envie seus dados e links para ter seu trabalho divulgado no <strong className="text-white">arrocha<span className="text-[#FF007F]">play</span></strong>. 
              Aceitamos CDs, músicas, EPs e DVDs do mundo do arrocha!
            </p>
          </div>

          {/* Sua Música Banner */}
          <a href="https://suamusica.com.br" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/20 border border-green-500/20 rounded-xl mb-8 hover:border-green-500/40 transition-all group"
            data-testid="sua-musica-link"
          >
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Music2 className="w-6 h-6 text-green-400" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-green-300">Sua Música</p>
              <p className="text-zinc-400 text-sm">Cadastre sua banda no suamusica.com.br e coloque o link abaixo para potencializar sua divulgação!</p>
            </div>
            <ExternalLink className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </a>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="divulgue-form">
            {/* Personal Info */}
            <div className="bg-[#111] border border-white/10 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#FF007F] rounded-full text-xs flex items-center justify-center font-bold">1</span>
                Seus Dados
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Seu Nome *</label>
                  <input name="name" value={form.name} onChange={handleChange} required
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    placeholder="João Silva" data-testid="input-name" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Nome da Banda/Artista *</label>
                  <input name="band_name" value={form.band_name} onChange={handleChange} required
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    placeholder="Banda XYZ" data-testid="input-band-name" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">E-mail *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    placeholder="contato@banda.com" data-testid="input-email" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">WhatsApp</label>
                  <input name="whatsapp" value={form.whatsapp} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    placeholder="(71) 99999-9999" data-testid="input-whatsapp" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Cidade</label>
                  <input name="city" value={form.city} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    placeholder="Salvador" data-testid="input-city" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Estado</label>
                  <select name="state" value={form.state} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    data-testid="select-state">
                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Music Info */}
            <div className="bg-[#111] border border-white/10 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#FF007F] rounded-full text-xs flex items-center justify-center font-bold">2</span>
                Sobre a Música
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Gênero Musical</label>
                  <select name="genre" value={form.genre} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    data-testid="select-genre">
                    {genres.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">O que deseja divulgar?</label>
                  <select name="submission_type" value={form.submission_type} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors"
                    data-testid="select-type">
                    {submissionTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="bg-[#111] border border-white/10 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#FF007F] rounded-full text-xs flex items-center justify-center font-bold">3</span>
                Links e Redes Sociais
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 flex items-center gap-2">
                    <Music2 size={14} className="text-green-400" /> Link do Sua Música
                  </label>
                  <input name="sua_musica_url" value={form.sua_musica_url} onChange={handleChange}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-green-400 focus:outline-none transition-colors"
                    placeholder="https://suamusica.com.br/suabanda" data-testid="input-sua-musica" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 flex items-center gap-2">
                      <Instagram size={14} className="text-pink-400" /> Instagram
                    </label>
                    <input name="instagram_url" value={form.instagram_url} onChange={handleChange}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-pink-400 focus:outline-none transition-colors"
                      placeholder="@suabanda" data-testid="input-instagram" />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 flex items-center gap-2">
                      <Youtube size={14} className="text-red-400" /> YouTube
                    </label>
                    <input name="youtube_url" value={form.youtube_url} onChange={handleChange}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-red-400 focus:outline-none transition-colors"
                      placeholder="youtube.com/@suabanda" data-testid="input-youtube" />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 flex items-center gap-2">
                      <Disc3 size={14} className="text-green-400" /> Spotify
                    </label>
                    <input name="spotify_url" value={form.spotify_url} onChange={handleChange}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-green-400 focus:outline-none transition-colors"
                      placeholder="open.spotify.com/artist/..." data-testid="input-spotify" />
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="bg-[#111] border border-white/10 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#FF007F] rounded-full text-xs flex items-center justify-center font-bold">4</span>
                Mensagem
              </h3>
              <textarea name="message" value={form.message} onChange={handleChange} rows={4}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none transition-colors resize-none"
                placeholder="Conte um pouco sobre a banda, história, próximos shows..." data-testid="input-message" />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-4 bg-[#FF007F] hover:bg-[#FF3399] disabled:opacity-50 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2"
              data-testid="submit-btn">
              {loading ? "Enviando..." : (
                <>
                  <Send size={20} />
                  Enviar Solicitação de Divulgação
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
